/**
 * Joystick-Logik für den Planeten-Button.
 * Gedrückt halten + ziehen = Richtung/Stärke bestimmen.
 * Die Kamera-Bewegung passiert extern im update()-Loop (nicht hier).
 */
const HOLD_MS = 180;
const ICON_MAX_PX = 12;
const DEAD_ZONE = 4;
const B = 'rgba(100,180,255,';
const BRD = B + '0.25)';
const GLOW_ACTIVE = `0 0 24px ${B}0.35)`;
const GLOW_DEFAULT = `0 0 20px ${B}0.1), inset 0 1px 0 ${B}0.12)`;

export class PlanetBtnPan {
  private _panning = false;
  private _holdTimer = 0;
  private _startX = 0;
  private _startY = 0;
  private _iconEl: HTMLElement | null = null;

  /** Joystick-Auslenkung: -1..+1 pro Achse (0 = neutral). */
  dirX = 0;
  dirY = 0;
  /** 0..1 wie weit der Stick ausgelenkt ist. */
  magnitude = 0;

  constructor(
    private _btn: HTMLDivElement,
    private _onTap: () => void,
  ) {
    _btn.addEventListener('pointerdown', (e) => this._onDown(e));
    _btn.addEventListener('pointermove', (e) => this._onMove(e));
    _btn.addEventListener('pointerup', (e) => this._onUp(e));
    _btn.addEventListener('pointercancel', () => this._cancel());
  }

  get panning(): boolean { return this._panning; }

  setIconEl(el: HTMLElement): void { this._iconEl = el; }

  dispose(): void {
    if (this._holdTimer) clearTimeout(this._holdTimer);
  }

  private _onDown(e: PointerEvent): void {
    e.stopPropagation();
    this._btn.setPointerCapture(e.pointerId);
    this._startX = e.clientX;
    this._startY = e.clientY;
    this._panning = false;
    this.dirX = 0; this.dirY = 0; this.magnitude = 0;
    this._holdTimer = window.setTimeout(() => this._enterPan(), HOLD_MS);
  }

  private _onMove(e: PointerEvent): void {
    const ox = e.clientX - this._startX, oy = e.clientY - this._startY;
    if (!this._panning) {
      if (ox * ox + oy * oy > DEAD_ZONE * DEAD_ZONE) {
        if (this._holdTimer) { clearTimeout(this._holdTimer); this._holdTimer = 0; }
        this._enterPan();
      }
      return;
    }
    const len = Math.sqrt(ox * ox + oy * oy);
    if (len < DEAD_ZONE) {
      this.dirX = 0; this.dirY = 0; this.magnitude = 0;
    } else {
      this.dirX = ox / len;
      this.dirY = oy / len;
      this.magnitude = 1;
    }
    this._updateIcon(ox, oy);
  }

  private _onUp(e: PointerEvent): void {
    this._btn.releasePointerCapture(e.pointerId);
    if (this._holdTimer) { clearTimeout(this._holdTimer); this._holdTimer = 0; }
    if (!this._panning) this._onTap();
    this._cancel();
  }

  private _enterPan(): void {
    this._panning = true;
    this._btn.style.borderColor = B + '0.8)';
    this._btn.style.boxShadow = GLOW_ACTIVE;
  }

  private _cancel(): void {
    if (this._holdTimer) { clearTimeout(this._holdTimer); this._holdTimer = 0; }
    this._panning = false;
    this.dirX = 0; this.dirY = 0; this.magnitude = 0;
    this._btn.style.borderColor = BRD;
    this._btn.style.boxShadow = GLOW_DEFAULT;
    if (this._iconEl) this._iconEl.style.transform = 'translate(0,0)';
  }

  private _updateIcon(ox: number, oy: number): void {
    if (!this._iconEl) return;
    const len = Math.sqrt(ox * ox + oy * oy);
    if (len < DEAD_ZONE) { this._iconEl.style.transform = 'translate(0,0)'; return; }
    const c = Math.min(len, ICON_MAX_PX) / len;
    this._iconEl.style.transform = `translate(${Math.round(ox * c)}px,${Math.round(oy * c)}px)`;
  }
}
